import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { Headphones, LoaderCircle, Pause, Play, Radio, Search, SkipBack, SkipForward, Volume2, X } from "lucide-react";
import { SiSpotify } from "react-icons/si";
import { axiosRadio } from "../../api/axios";
import "./NavbarRadio.css";

const musicStationPattern = /tarab|tounsi|gold|music|muzik|dance|classique|arabic/i;
const SPOTIFY_RECENTS_KEY = "medilink.spotify.recents.v1";

const getStreamUrl = (station) => station.url_resolved || station.url;
const getStationId = (station) => station.stationuuid || station.url_resolved || station.url;
const getStationRegion = (station) => {
  const state = station.state?.trim();
  return state && !/^(https?:\/\/|www\.)/i.test(state) ? state : station.countrycode || "TN";
};
const getPlaybackErrorKey = (error) => {
  if (error?.name === "NotAllowedError") return "playbackBlocked";
  if (error?.name === "NotSupportedError") return "playbackUnsupported";
  if (["stream-timeout", "stream-network-error"].includes(error?.message)) return "playbackNetworkError";
  return "playbackError";
};
const readSpotifyRecents = () => {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(SPOTIFY_RECENTS_KEY) || "[]");
    return Array.isArray(stored) ? stored.filter((item) => item?.embedPath && item?.url).slice(0, 8) : [];
  } catch {
    return [];
  }
};
const getSpotifyEmbedPath = (value) => {
  const raw = String(value || "").trim();
  const uriMatch = raw.match(/^spotify:(track|album|playlist|artist|show|episode):([a-z\d]+)$/i);
  if (uriMatch) return `${uriMatch[1].toLowerCase()}/${uriMatch[2]}`;

  try {
    const url = new URL(raw);
    if (!["open.spotify.com", "play.spotify.com"].includes(url.hostname.toLowerCase())) return null;
    const parts = url.pathname.split("/").filter(Boolean);
    const resourceIndex = parts.findIndex((part) => ["track", "album", "playlist", "artist", "show", "episode"].includes(part));
    const id = parts[resourceIndex + 1];
    if (resourceIndex < 0 || !/^[a-z\d]+$/i.test(id || "")) return null;
    return `${parts[resourceIndex]}/${id}`;
  } catch {
    return null;
  }
};

export default function NavbarRadio() {
  const { t, i18n } = useTranslation("radio");
  const { pathname } = useLocation();
  const normalizedPath = pathname.replace(/\/+$/, "") || "/";
  const isWaitingRoomRoute = normalizedPath === "/waiting-room"
    || normalizedPath.startsWith("/waiting-room/")
    || normalizedPath === "/assistant/waiting-room"
    || normalizedPath.startsWith("/assistant/waiting-room/");
  const [isOpen, setIsOpen] = useState(false);
  const [playbackMode, setPlaybackMode] = useState("radio");
  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [messageKey, setMessageKey] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [spotifyLink, setSpotifyLink] = useState("");
  const [spotifyEmbedPath, setSpotifyEmbedPath] = useState("");
  const [spotifyLinkError, setSpotifyLinkError] = useState(false);
  const [spotifyRecentItems, setSpotifyRecentItems] = useState(readSpotifyRecents);
  const audioRef = useRef(null);
  const playbackAttemptRef = useRef(0);
  const selectedStation = stations.find((station) => getStationId(station) === selectedId);
  const isSelectedStationPlaying = Boolean(isPlaying && currentStation && selectedId === getStationId(currentStation));

  const handlePlaybackModeChange = (mode) => {
    if (mode === "spotify") audioRef.current?.pause();
    setPlaybackMode(mode);
  };

  const handleSpotifySubmit = (event) => {
    event.preventDefault();
    const embedPath = getSpotifyEmbedPath(spotifyLink);
    setSpotifyLinkError(!embedPath);
    if (!embedPath) return;
    const item = { embedPath, url: spotifyLink.trim() };
    setSpotifyEmbedPath(embedPath);
    setSpotifyRecentItems((current) => {
      const updated = [item, ...current.filter((entry) => entry.embedPath !== embedPath)].slice(0, 8);
      try {
        window.localStorage.setItem(SPOTIFY_RECENTS_KEY, JSON.stringify(updated));
      } catch {
        // Keep the current session usable when browser storage is unavailable.
      }
      return updated;
    });
  };

  useEffect(() => {
    if (!isOpen || stations.length) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(false);
    axiosRadio.get("/stations/bycountrycodeexact/TN", {
      params: { hidebroken: true, order: "clickcount", reverse: true, limit: 100 },
      signal: controller.signal,
    })
      .then(({ data: items }) => {
        if (!Array.isArray(items)) throw new Error("Station directory returned invalid data");
        return items;
      })
      .then((items) => {
        const secureOnly = window.location.protocol === "https:";
        const uniqueStations = new Map();
        items.forEach((station) => {
          const streamUrl = getStreamUrl(station);
          if (!station.name || !streamUrl || (secureOnly && !streamUrl.startsWith("https://"))) return;
          uniqueStations.set(getStationId(station), station);
        });
        setStations([...uniqueStations.values()]);
      })
      .catch((error) => {
        if (error.name !== "AbortError") setLoadError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [isOpen, retryCount, stations.length]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  useEffect(() => () => audioRef.current?.pause(), []);

  useEffect(() => {
    if (isWaitingRoomRoute || (!isOpen && !isPlaying)) return;
    audioRef.current?.pause();
    setIsPlaying(false);
    setIsOpen(false);
  }, [isOpen, isPlaying, isWaitingRoomRoute]);

  const filteredStations = useMemo(() => {
    const query = searchValue.trim().toLocaleLowerCase();
    if (!query) return stations;
    return stations.filter((station) => `${station.name} ${station.state || ""} ${station.tags || ""}`.toLocaleLowerCase().includes(query));
  }, [searchValue, stations]);

  const playStation = async (station) => {
    const audio = audioRef.current;
    if (!audio || !station) return;
    const attemptId = playbackAttemptRef.current + 1;
    playbackAttemptRef.current = attemptId;
    setSelectedId(getStationId(station));
    setCurrentStation(station);
    setMessageKey("");
    audio.pause();
    const isSecurePage = window.location.protocol === "https:";
    const streamUrls = [...new Set([station.url_resolved, station.url].filter((url) => (
      typeof url === "string" && url.trim() && (!isSecurePage || url.startsWith("https://"))
    )))];
    let lastError;

    for (const streamUrl of streamUrls) {
      try {
        await new Promise((resolve, reject) => {
          let timeoutId;
          const cleanup = () => {
            window.clearTimeout(timeoutId);
            audio.removeEventListener("playing", onPlaying);
            audio.removeEventListener("error", onError);
          };
          const onPlaying = () => {
            cleanup();
            resolve();
          };
          const onError = () => {
            const code = audio.error?.code;
            const error = new Error(code === 2 ? "stream-network-error" : "stream-media-error");
            if ([3, 4].includes(code)) error.name = "NotSupportedError";
            cleanup();
            reject(error);
          };

          audio.addEventListener("playing", onPlaying, { once: true });
          audio.addEventListener("error", onError, { once: true });
          timeoutId = window.setTimeout(() => {
            cleanup();
            reject(new Error("stream-timeout"));
          }, 12000);
          audio.src = streamUrl;
          audio.load();
          audio.play().catch((error) => {
            cleanup();
            reject(error);
          });
        });
        if (playbackAttemptRef.current !== attemptId) return;
        playbackAttemptRef.current = 0;
        setMessageKey("");
        setIsPlaying(true);
        return;
      } catch (error) {
        if (playbackAttemptRef.current !== attemptId) return;
        lastError = error;
        audio.pause();
        if (error?.name === "NotAllowedError") break;
      }
    }

    if (playbackAttemptRef.current !== attemptId) return;
    playbackAttemptRef.current = 0;
    setIsPlaying(false);
    setMessageKey(getPlaybackErrorKey(lastError));
  };

  const togglePlayback = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    const hasSelectedDifferentStation = selectedStation
      && currentStation
      && getStationId(selectedStation) !== getStationId(currentStation);
    if (isPlaying && !hasSelectedDifferentStation) {
      audio.pause();
      setIsPlaying(false);
      return;
    }
    if (!hasSelectedDifferentStation && currentStation && selectedId === getStationId(currentStation) && audio.src) {
      try {
        await audio.play();
        setMessageKey("");
        setIsPlaying(true);
      } catch {
        setMessageKey("playbackError");
      }
      return;
    }
    await playStation(selectedStation);
  };

  const playAdjacentStation = (direction) => {
    if (!stations.length) return;
    const currentIndex = stations.findIndex((station) => getStationId(station) === selectedId);
    const nextIndex = currentIndex < 0
      ? (direction > 0 ? 0 : stations.length - 1)
      : (currentIndex + direction + stations.length) % stations.length;
    playStation(stations[nextIndex]);
  };

  const handleStationClick = (station) => {
    const isCurrentStation = currentStation && getStationId(currentStation) === getStationId(station);
    if (isCurrentStation && isPlaying) {
      audioRef.current?.pause();
      setSelectedId(getStationId(station));
      setIsPlaying(false);
      return;
    }
    playStation(station);
  };

  const groupedStations = [
    { key: "general", items: filteredStations.filter((station) => !musicStationPattern.test(`${station.name} ${station.tags || ""}`)) },
    { key: "music", items: filteredStations.filter((station) => musicStationPattern.test(`${station.name} ${station.tags || ""}`)) },
  ];

  return (
    <>
      {isWaitingRoomRoute && <button
          type="button"
          className={`navbar__icon-button navbar-radio__trigger${isPlaying ? " is-playing" : ""}`}
          aria-label={t("open")}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          title={t("open")}
          onClick={() => setIsOpen(true)}
        >
          <Headphones size={19} aria-hidden="true" />
        </button>}
      <audio
        ref={audioRef}
        className="navbar-radio__audio"
        preload="none"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onError={(event) => {
          setIsPlaying(false);
          if (playbackAttemptRef.current) return;
          const errorCode = event.currentTarget.error?.code;
          if (errorCode === 1) return;
          setMessageKey(errorCode === 2 ? "playbackNetworkError" : [3, 4].includes(errorCode) ? "playbackUnsupported" : "playbackError");
        }}
      />
      {isOpen && createPortal(
        <div className="navbar-radio__backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setIsOpen(false);
        }}>
          <section className="navbar-radio__dialog" role="dialog" aria-modal="true" aria-labelledby="navbar-radio-title" dir={i18n.dir()}>
            <header className="navbar-radio__header">
              <div>
                <span className="navbar-radio__eyebrow"><span />{t("live")}</span>
                <h2 id="navbar-radio-title">{t("title")}</h2>
                <p>{t("subtitle")}</p>
              </div>
              <button type="button" className="navbar-radio__close" aria-label={t("close")} onClick={() => setIsOpen(false)}><X size={18} /></button>
            </header>

            <div className="navbar-radio__player">
              <div className="navbar-radio__source-tabs" role="group" aria-label={t("sourceModeLabel")}>
                <button type="button" className={playbackMode === "radio" ? "is-active" : ""} aria-pressed={playbackMode === "radio"} onClick={() => handlePlaybackModeChange("radio")}>
                  <Radio size={16} aria-hidden="true" />{t("radioMode")}
                </button>
                <button type="button" className={playbackMode === "spotify" ? "is-active" : ""} aria-pressed={playbackMode === "spotify"} onClick={() => handlePlaybackModeChange("spotify")}>
                  <SiSpotify size={17} aria-hidden="true" />{t("spotifyMode")}
                </button>
              </div>

              {playbackMode === "radio" && <>
                <div className="navbar-radio__transport">
                  <button type="button" aria-label={t("previous")} title={t("previous")} disabled={!stations.length} onClick={() => playAdjacentStation(-1)}><SkipBack size={16} /></button>
                  <button type="button" aria-label={t(isSelectedStationPlaying ? "pause" : "play")} title={t(isSelectedStationPlaying ? "pause" : "play")} disabled={!selectedStation && !currentStation} onClick={togglePlayback}>
                    {isSelectedStationPlaying ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" />}
                  </button>
                  <button type="button" aria-label={t("next")} title={t("next")} disabled={!stations.length} onClick={() => playAdjacentStation(1)}><SkipForward size={16} /></button>
                </div>
                <div className="navbar-radio__volume">
                  <Volume2 size={17} aria-hidden="true" />
                  <input type="range" min="0" max="1" step="0.01" value={volume} aria-label={t("volume")} onChange={(event) => setVolume(Number(event.target.value))} />
                </div>
              </>}

              {playbackMode === "radio" ? <>
                {currentStation && <div className="navbar-radio__now-playing"><span>{t("nowPlaying")}</span><strong>{currentStation.name}</strong></div>}
                {messageKey && <div className="navbar-radio__message" role="status"><span>{t(messageKey)}</span>{currentStation && <button type="button" onClick={() => playStation(currentStation)}>{t("retry")}</button>}</div>}
              </> : <div className="navbar-radio__spotify-content">
                <form className="navbar-radio__spotify-form" onSubmit={handleSpotifySubmit}>
                  <label htmlFor="navbar-radio-spotify-link">{t("spotifyLinkLabel")}</label>
                  <div>
                    <input id="navbar-radio-spotify-link" type="text" inputMode="url" autoComplete="url" value={spotifyLink} placeholder={t("spotifyLinkPlaceholder")} onChange={(event) => {
                      setSpotifyLink(event.target.value);
                      setSpotifyLinkError(false);
                    }} />
                    <button type="submit" disabled={!spotifyLink.trim()}>{t("spotifyLoad")}</button>
                  </div>
                </form>
                <p className="navbar-radio__spotify-hint">{t("spotifyHint")}</p>
                {spotifyLinkError && <p className="navbar-radio__spotify-error" role="alert">{t("spotifyInvalid")}</p>}
                {spotifyEmbedPath ? <iframe
                  className="navbar-radio__spotify-embed"
                  title={t("spotifyPlayerTitle")}
                  src={`https://open.spotify.com/embed/${spotifyEmbedPath}?utm_source=generator&theme=0`}
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                /> : <div className="navbar-radio__spotify-placeholder"><SiSpotify size={26} aria-hidden="true" /><span>{t("spotifyEmpty")}</span></div>}
                <section className="navbar-radio__spotify-recents" aria-label={t("spotifyRecent")}>
                  <h3>{t("spotifyRecent")}</h3>
                  {spotifyRecentItems.length ? spotifyRecentItems.map((item) => {
                    const [type, id] = item.embedPath.split("/");
                    return <button type="button" className="navbar-radio__spotify-recent-item" key={item.embedPath} onClick={() => {
                      setSpotifyLink(item.url);
                      setSpotifyEmbedPath(item.embedPath);
                      setSpotifyLinkError(false);
                    }}>
                      <SiSpotify size={16} aria-hidden="true" />
                      <span><strong>{t(`spotifyTypes.${type}`)}</strong><small>{id}</small></span>
                      <Play size={14} aria-hidden="true" />
                    </button>;
                  }) : <p>{t("spotifyNoRecent")}</p>}
                </section>
              </div>}
            </div>

            {playbackMode === "radio" && <div className="navbar-radio__search">
              <Search size={16} aria-hidden="true" />
              <input type="search" value={searchValue} aria-label={t("searchLabel")} placeholder={t("searchPlaceholder")} onChange={(event) => setSearchValue(event.target.value)} />
            </div>}

            {playbackMode === "radio" && <div className="navbar-radio__stations" aria-live="polite">
              {isLoading && <div className="navbar-radio__empty"><LoaderCircle size={17} className="navbar-radio__spinner" />{t("loading")}</div>}
              {loadError && <div className="navbar-radio__empty"><span>{t("loadError")}</span><button type="button" onClick={() => setRetryCount((count) => count + 1)}>{t("retry")}</button></div>}
              {!isLoading && !loadError && filteredStations.length === 0 && <div className="navbar-radio__empty">{t(stations.length ? "noResults" : "noStations")}</div>}
              {!isLoading && !loadError && groupedStations.map((group) => group.items.length > 0 && (
                <section className="navbar-radio__group" key={group.key}>
                  <h3>{t(group.key)}</h3>
                  {group.items.map((station) => {
                    const isCurrent = currentStation && getStationId(currentStation) === getStationId(station);
                    const isStationPlaying = Boolean(isCurrent && isPlaying);
                    return <button type="button" className={`navbar-radio__station${isCurrent ? " is-current" : ""}${isStationPlaying ? " is-playing" : ""}`} key={getStationId(station)} aria-label={`${station.name}: ${t(isStationPlaying ? "pause" : "play")}`} onClick={() => handleStationClick(station)}>
                      <span className="navbar-radio__station-meta" title={getStationRegion(station)}>{getStationRegion(station)}</span>
                      <span className="navbar-radio__station-info"><strong>{station.name}</strong><small className={isStationPlaying ? "is-live" : ""}>{isStationPlaying ? <><span className="navbar-radio__live-dot" />{t("live")}</> : station.tags?.split(",").slice(0, 3).join(" · ") || t("tunisianStation")}</small></span>
                      <span className="navbar-radio__station-action" aria-hidden="true">{isStationPlaying ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" />}</span>
                    </button>;
                  })}
                </section>
              ))}
            </div>}
          </section>
        </div>,
        document.body
      )}
    </>
  );
}
